import React from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { PageHeader, ResponsiveTable, TableSkeleton, EmptyState, TabList, StatusBadge, AutoBadge, ReasonModal, DateCell } from "@/admin/ui";
import { CheckCircle2, XCircle, ShieldAlert } from "lucide-react";

const fmt$ = (cents) => `$${((Number(cents || 0)) / 100).toFixed(2)}`;

export default function AdminCases() {
  const { t } = useLang();
  const [tab, setTab] = React.useState("open");
  const [cases, setCases] = React.useState(null);
  const [modal, setModal] = React.useState(null);

  const load = React.useCallback(() => {
    const params = tab === "all" ? {} : { status: tab };
    api.get("/api/admin/cases", { params }).then((r) => setCases(r.data.items));
  }, [tab]);
  React.useEffect(() => { load(); }, [load]);

  const openApprove = (c) => setModal({
    title: t("admin.cases.approve_title"),
    description: t("admin.cases.approve_desc", { credits: c.needed_credits?.toLocaleString(), user: c.user?.email || c.user_id }),
    actionLabel: t("admin.cases.confirm_approve"),
    onSubmit: async (reason) => {
      const r = await api.post(`/api/admin/cases/${c.id}/approve`, { reason });
      toast.success(`${t("admin.cases.approved")}: ${r.data.credits_reversed?.toLocaleString?.() || 0} credits`);
      load();
    },
  });
  const openDismiss = (c) => setModal({
    title: t("admin.cases.dismiss_title"), description: t("admin.cases.dismiss_desc"),
    actionLabel: t("admin.cases.confirm_dismiss"), danger: true,
    onSubmit: async (reason) => {
      await api.post(`/api/admin/cases/${c.id}/dismiss`, { note: reason, reason });
      toast.success(t("admin.cases.dismissed"));
      load();
    },
  });
  const openResolve = (c, outcome) => setModal({
    title: t("admin.cases.resolve_title"), description: `${t("admin.cases.resolve_desc")} (${outcome})`,
    actionLabel: outcome === "won" ? t("admin.cases.won") : t("admin.cases.lost"),
    danger: outcome === "lost",
    onSubmit: async (reason) => {
      const disputesResp = await api.get("/api/admin/disputes");
      const d = disputesResp.data.items.find((x) => x.creem_dispute_id === c.creem_dispute_id);
      if (!d) throw new Error("Dispute record not found");
      await api.post(`/api/admin/disputes/${d.id}/resolve-v2`, { outcome, note: reason });
      toast.success(`${t("admin.cases.dispute_resolved")} (${outcome})`);
      load();
    },
  });

  return (
    <div className="space-y-6" data-testid="admin-cases-page">
      <PageHeader title={t("admin.cases.title")} description={t("admin.cases.subtitle")} />
      <TabList value={tab} onChange={setTab} tabs={["open", "resolved", "all"].map((s) => ({ id: s, label: t(`admin.cases.tab.${s}`) }))} />

      {!cases ? <TableSkeleton rows={6} cols={8} /> : cases.length === 0 ? (
        <EmptyState title={t("admin.cases.empty")} />
      ) : (
        <ResponsiveTable minWidth={1000}>
          <thead className="lp-table-sticky"><tr>
            <th>{t("admin.cases.col.at")}</th>
            <th>{t("admin.cases.col.kind")}</th>
            <th>{t("admin.cases.col.user")}</th>
            <th>{t("admin.cases.col.invoice")}</th>
            <th className="text-right">{t("admin.cases.col.amount")}</th>
            <th className="text-right">{t("admin.cases.col.needed_credits")}</th>
            <th>{t("admin.cases.col.status")}</th>
            <th className="text-right"></th>
          </tr></thead>
          <tbody>
            {cases.map((c) => (
              <tr key={c.id} data-testid={`case-${c.id}`}>
                <td><DateCell value={c.created_at} /></td>
                <td><StatusBadge tone={c.kind === "dispute_open" ? "danger" : "warning"}>{c.kind}</StatusBadge></td>
                <td className="text-white/85 text-xs truncate max-w-[220px]" title={c.user?.email || c.user_id}>{c.user?.email || c.user_id}</td>
                <td className="font-mono text-white/70 text-xs">{c.invoice?.invoice_number || c.invoice_id || "—"}</td>
                <td className="text-right tabular-nums">{fmt$(c.amount_cents)}</td>
                <td className="text-right text-white/70 tabular-nums">{c.needed_credits ? c.needed_credits.toLocaleString() : "—"}</td>
                <td><AutoBadge value={c.status} /></td>
                <td className="text-right">
                  {c.status === "open" && c.kind === "refund_insufficient_balance" && (
                    <div className="inline-flex gap-1">
                      <button className="lp-btn-ghost" onClick={() => openApprove(c)} data-testid={`case-approve-${c.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> {t("admin.cases.approve")}
                      </button>
                      <button className="lp-btn-ghost" onClick={() => openDismiss(c)} data-testid={`case-dismiss-${c.id}`}>
                        <XCircle className="w-3.5 h-3.5" /> {t("admin.cases.dismiss")}
                      </button>
                    </div>
                  )}
                  {c.status === "open" && c.kind === "dispute_open" && (
                    <div className="inline-flex gap-1">
                      <button className="lp-btn-ghost" onClick={() => openResolve(c, "won")} data-testid={`case-resolve-won-${c.id}`}>
                        <ShieldAlert className="w-3.5 h-3.5" /> {t("admin.cases.won")}
                      </button>
                      <button className="lp-btn-ghost" onClick={() => openResolve(c, "lost")} data-testid={`case-resolve-lost-${c.id}`}>
                        <ShieldAlert className="w-3.5 h-3.5" /> {t("admin.cases.lost")}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      )}
      <ReasonModal open={!!modal} {...(modal || {})} onClose={() => setModal(null)} onSubmit={modal?.onSubmit || (async () => {})} />
    </div>
  );
}
